import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

// import SortableList, { SortableItem } from 'react-easy-sort'
import {
    closestCenter,
    DndContext,
    DragStartEvent,
    DragOverlay,
} from '@dnd-kit/core';
import {
    verticalListSortingStrategy,
    SortableContext,
} from '@dnd-kit/sortable';

import Stack from '@mui/joy/Stack';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import Typography from '@mui/joy/Typography';
import Box from '@mui/joy/Box';
import Divider from '@mui/joy/Divider';

import useProject from 'hooks/useProject';
import useUserIdentity from 'hooks/useUserIdentity';
import useDedicatedAssignment from 'hooks/useDedicatedAssignment';
import useAuth from 'hooks/useAuth';
import useWorkKey from 'hooks/useWorkKey';

import { ProjectProps } from 'stores/project_store';
import ListicleProject from 'components/ListicleProject';

import { SUGGESTED } from 'utils/suggestedProjects';
import useSortable from 'hooks/useSortable';
import { UuidType } from 'utils/types';
import SortableItem from 'components/SortableItem';
import Error from 'components/Error';


const ListicleProjectForm = ({
    expanded=undefined,
    onProjectCreated=undefined,
    identity
}:
{expanded?: boolean,
    suggested?: string[],
    onProjectCreated?: (project: ProjectProps) => void,
    identity?: string

}) => {
    const { createProject, createProjectByDedicated } = useProject();
    const { currentIdentity } = useUserIdentity();
    const workKey = useWorkKey();
    const { currentUser } = useAuth();
    const [title, setTitle] = useState('');
    const [saving, setSaving] = useState(false);
    const [expandedState, setExpandedState] = useState(expanded);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const { hasDedicatedAssistant } = useDedicatedAssignment();
    const INITIAL_LIMIT = 13;
    const [currentLimit, setCurrentLimit] = useState(INITIAL_LIMIT);

    const suggested = SUGGESTED.slice(0, currentLimit);
    const PAGE_SIZE = 20;

    if(!identity) return;

    function getVisibility() {
        const PRIVATE_VISIBILITY = 0;
        const DEDICATED_VISIBILITY = 1;

        // If it's an assistant creating this, it's dedicated
        if(currentIdentity.role == 'assistant')
            return DEDICATED_VISIBILITY;

        // If the user has a dedicated assistant: dedicated
        if(currentUser?.subscription_product == "dedicated")
            return DEDICATED_VISIBILITY;

        // If the current user have a dedicated assistant
        if(hasDedicatedAssistant(currentIdentity?.uuid))
            return DEDICATED_VISIBILITY;

        // otherwise privde
        return PRIVATE_VISIBILITY;
    }

    const doCreate = async (project: ProjectProps) => {
        if (currentIdentity.role == 'assistant') {
            if(!workKey) {
                Error('No identity loaded');
                return;
            };
            return createProjectByDedicated(workKey, project);
        }
        return createProject(project);
    }

    const doCreateProject = () => {
        // The project is owned by the current user unless we're the dedicated
        const project_identity = (currentIdentity.role == 'assistant' ?
            identity : currentIdentity.uuid);

        // Assume private
        const project = {
            title: title.trim(),
            cadence: 'chat',
            identity: project_identity,
            visibility: getVisibility(),
        } as ProjectProps;
        setSaving(true);

        doCreate(project)
            .then((project: ProjectProps | null | undefined) => {
                if(project) {
                    onProjectCreated && onProjectCreated(project);
                }
                setExpandedState(false);
            })
            .catch(() => {
                Error("Unable to create project");
            })
            .finally(() => {
                setSaving(false);
            });
        setTitle('');
    };
    if(!expandedState) {
        return (
            <Stack direction="row">
            <Button
                variant="plain"
                onClick={() => setExpandedState(true)}
            >
                New Project
                                   </Button>
                                   </Stack>
        );
    }
    return (
        <Stack
            spacing={3}
            m={2}>

            <Input
                onChange={(e) => { setTitle(e.target.value); }}
                value={title}
                name="title"
                type="title"
                sx={{ fontSize: '1.5rem' }}
                placeholder="&quot;Q4 Marketing Tasks&quot; or &quot;🌻 Yard&quot; or &quot;Patio Rebuild&quot;"
            />

            <Stack
                spacing={1}
                justifyContent="space-between"
                direction="row"
            >
                {expanded === undefined && expandedState &&
                    <Button
                        variant="plain"
                        onClick={() => setExpandedState(false)}
                    >
                        Cancel
                    </Button>
                }
                <Box flexGrow={1} />
                <Button
                    disabled={title.trim().length === 0}
                    loading={saving}
                    onClick={() => doCreateProject()}
                >
                    Save
                </Button>
            </Stack>

            { !showSuggestions  &&
                <Button
                    variant="plain"
                    onClick={() => setShowSuggestions(true)}
                >
                     Show Project Name Suggestions
                </Button>
            }
            { showSuggestions && suggested.length > 0 &&
                <Stack
                    alignItems="center"
                    spacing={3}
                >
                    <Typography
                     level="body-xs"
                     onClick={() => setShowSuggestions(false)}
                     sx={{ cursor: 'pointer' }}
                    >
                     Suggestions for a project name
                    </Typography>
                    <Stack spacing={1} direction="row" flexWrap="wrap" useFlexGap>
                    { suggested.map((s) =>
                        <Chip key={s} onClick={() => setTitle(s)}>{s}</Chip>
                    )}
                    </Stack>
                </Stack>
            }
            { showSuggestions && currentLimit > INITIAL_LIMIT &&
                <Button
                    variant="plain"
                    size="sm"
                    onClick={() => setCurrentLimit(currentLimit - PAGE_SIZE)}
                >
                    Fewer
                </Button>
            }
            { showSuggestions && currentLimit < SUGGESTED.length &&
                <Button
                    fullWidth
                    variant="outlined"
                    size="sm"
                    onClick={() => setCurrentLimit(currentLimit + PAGE_SIZE)}
                >
                    Show More
                </Button>
            }

        </Stack>
    );
};

const ListicleEmpty = ({onProjectCreated=undefined, identity}: { onProjectCreated?: (project: ProjectProps) => void, identity: string}) => {
    return (
        <Stack
            spacing={1}
            m={2}>
            <Stack
                spacing={1}
                pb={2}
            >
            <Typography
                level="title-lg"
            >
                Let's create project to stay organized
            </Typography>
            <Typography
                level="body-sm"
            >
                We keep things organized in projects.
                You can create many projects or work out of the same one every time.

            </Typography>
            </Stack>
            <ListicleProjectForm
                identity={identity}
                onProjectCreated={onProjectCreated}
                expanded={true} />

            <Stack spacing={1} pt={1}>
            </Stack>

        </Stack>
    );
};

export const SortableListicle = observer(({identity}:{identity: string}) => {
    const {
        onSortEnd,
        items,
        updateItems,
        sensors
    } = useSortable('projects');
    const [activeId, setActiveId] = useState('');

    const {
        getProject,
        getIdentityProjects,
        fetchIdentityProjects
    } = useProject();
    const [fetching, setFetching] = useState(false);

    const onProjectCreated = (project: ProjectProps) => {
        updateItems([...items, project]);
    };

    function handleDragStart(event: DragStartEvent) {
        const { active } = event;
        setActiveId(active.id.toString());
    }

    useEffect(() => {
        if(!fetching) {
            setFetching(true);
            fetchIdentityProjects(identity)
                .then(() => {
                    setFetching(false);
                });
        }
    }, [identity]);

    useEffect(() => {
        console.log("Getting new projects")
        updateItems(getIdentityProjects(identity));
    }, [getIdentityProjects(identity).length]);

    const projects = items;
    const activeProject = activeId ? getProject(activeId) : null;
    return (
        <Stack ml={1}>
            {!fetching && projects.length === 0 &&
                <ListicleEmpty
                    identity={identity}
                    onProjectCreated={onProjectCreated} />
            }
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={onSortEnd}
            >
                <SortableContext
                    items={items.map((p) => p.uuid)}
                    strategy={verticalListSortingStrategy}
                >
                    {items.map((p: UuidType) =>
                        <SortableItem key={p.uuid} id={p.uuid}>
                            <Stack direction="row" py={3}>
                                <ListicleProject
                                    project={p as ProjectProps} />
                            </Stack>
                        </SortableItem>)
                    }
                </SortableContext>
                <DragOverlay>
                    {activeProject ?
                        <ListicleProject project={activeProject} /> :
                        null}
                </DragOverlay>
            </DndContext>

            {projects.length > 0 &&
                <Stack p={2} spacing={2}>
                    <Divider />
                    <ListicleProjectForm
                        identity={identity}
                        suggested={SUGGESTED}
                        onProjectCreated={onProjectCreated} />
                </Stack>
            }
        </Stack>
  );
});

const Listicle = observer(() => {
    const { currentIdentity } = useUserIdentity();
    if(!currentIdentity?.uuid) {
        return null;
    }
    return (
        <SortableListicle identity={currentIdentity.uuid} />
    );
});
export default Listicle;
